import { tlwTwMerge } from '@trg/utils';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
} from 'react';
import { useProductCardContext } from './ProductCardContext';
import { useLayoutStore } from '../../../layout';
import { SpecLabel } from '../SpecLabel';
import clsx from 'clsx';

export type ProductCardSpecsProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
>;

export function ProductCardSpecs({
  className,
  ...props
}: ProductCardSpecsProps) {
  const isMobile = useLayoutStore.use.isMobile();
  const {
    swatches,
    currentSwatch,
    isVertical,
    isHorizontal,
    setCurrentSwatch,
  } = useProductCardContext();

  const isStyleSwatch = swatches.some((item) => item.colorImage);
  const maxStyleSwatchCount = isMobile ? (isVertical ? 4 : 5) : 6;

  const judgeMoreVisibility = useCallback(
    () => (isStyleSwatch ? swatches.length > maxStyleSwatchCount : true),
    [isStyleSwatch, maxStyleSwatchCount, swatches.length]
  );

  const rafRef = useRef<number | null>(null);
  const sizeSwatchesRef = useRef<HTMLDivElement>(null);
  const [isMoreVisible, setMoreVisible] = useState(() => judgeMoreVisibility());

  useEffect(() => setMoreVisible(judgeMoreVisibility()), [judgeMoreVisibility]);

  useEffect(() => {
    const parentElement = sizeSwatchesRef.current?.parentElement;
    if (!parentElement) return;

    const resizeObserverCallback = () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(() => {
        const target = sizeSwatchesRef.current;
        if (!target) return setMoreVisible(false);

        const { clientWidth, scrollWidth, scrollHeight } = target;
        if (isVertical) {
          const maxHeight = parseInt(getComputedStyle(target).maxHeight, 10);
          setMoreVisible(scrollHeight > maxHeight);
        } else {
          setMoreVisible(scrollWidth > clientWidth);
        }
      });
    };

    const observer = new ResizeObserver(resizeObserverCallback);
    observer.observe(parentElement);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      observer.disconnect();
    };
  }, [isVertical]);

  return (
    <div
      className={tlwTwMerge(
        'tlw-flex tlw-items-center tlw-gap-1',
        isVertical && 'tlw-flex-wrap',
        className
      )}
      {...props}
    >
      {isStyleSwatch ? (
        swatches.slice(0, maxStyleSwatchCount).map((item) => (
          <SpecLabel
            key={item.sku}
            type="color"
            active={item.sku === currentSwatch.sku}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setCurrentSwatch(item);
            }}
          >
            <img
              className="tlw-size-3 tlw-rounded-full"
              src={item.colorImage as string}
              alt={item.colorName as string}
            />
          </SpecLabel>
        ))
      ) : (
        <div
          ref={sizeSwatchesRef}
          className={clsx('tlw-flex tlw-gap-1 tlw-overflow-hidden', {
            'tlw-flex-wrap tlw-max-h-[26px] lg:tlw-max-h-[25px]': isVertical,
          })}
        >
          {swatches.map((item) => (
            <SpecLabel
              key={item.sku}
              className={clsx(isHorizontal && 'tlw-shrink-0')}
              type="text"
              active={item.sku === currentSwatch.sku}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setCurrentSwatch(item);
              }}
            >
              {item.spec}
            </SpecLabel>
          ))}
        </div>
      )}

      <button
        className={clsx(
          'tlw-txt-zh-mini tlw-text-txt-700 tlw-px-0.5 tlw-underline hover:tlw-text-txt-core active:tlw-text-txt-core',
          isHorizontal && 'tlw-shrink-0'
        )}
        hidden={!isMoreVisible}
      >
        + More
      </button>
    </div>
  );
}
